/* East Coast Swell — behaviour for the custom sections (sw- prefix).
   Re-initialises on theme editor section reloads. Talks to Horizon's own
   cart drawer through Shopify's standard cart events, so the drawer, cart
   page and checkout stay Shopify-native. */

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CFG = window.ECS || {};
let Events = null;
const eventsReady = import('@shopify/events').then((m) => (Events = m)).catch(() => null);

/* ---------- money ---------- */
function formatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const fmt = format || CFG.moneyFormat || '${{amount}}';
  const rx = /\{\{\s*(\w+)\s*\}\}/;
  const withDelims = (n, p, t, d) => {
    if (isNaN(n) || n == null) return '0';
    const num = (n / 100.0).toFixed(p);
    const parts = num.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t);
    return dollars + (parts[1] ? d + parts[1] : '');
  };
  const m = fmt.match(rx);
  if (!m) return fmt;
  let v = '';
  switch (m[1]) {
    case 'amount_no_decimals': v = withDelims(cents, 0, ',', '.'); break;
    case 'amount_with_comma_separator': v = withDelims(cents, 2, '.', ','); break;
    case 'amount_no_decimals_with_comma_separator': v = withDelims(cents, 0, '.', ','); break;
    case 'amount_with_apostrophe_separator': v = withDelims(cents, 2, "'", '.'); break;
    default: v = withDelims(cents, 2, ',', '.');
  }
  return fmt.replace(rx, v);
}

/* ---------- cart ---------- */
async function refreshCount() {
  try {
    const r = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } });
    const cart = await r.json();
    document.querySelectorAll('[data-sw-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = !cart.item_count;
    });
    return cart;
  } catch (e) { return null; }
}

function openCartDrawer() {
  const drawer = document.querySelector('#cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    if (!drawer.isOpen) drawer.open();
    return true;
  }
  return false;
}

async function addToCart(items, sourceEl) {
  await eventsReady;
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset && el.dataset.sectionId)
    .filter(Boolean);
  const qty = items.reduce((a, i) => a + (i.quantity || 1), 0);
  let deferred = null;
  if (Events && Events.CartLinesUpdateEvent) {
    try {
      deferred = Events.CartLinesUpdateEvent.createPromise();
      (sourceEl || document).dispatchEvent(
        new Events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity || 1 })),
          promise: deferred.promise,
        })
      );
    } catch (e) { deferred = null; }
  }
  const res = await fetch(ROOT + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify({ items, sections: sectionIds.join(',') }),
  });
  const data = await res.json();
  const cart = await (async () => {
    try { return await (await fetch(ROOT + 'cart.js')).json(); } catch (e) { return null; }
  })();
  if (data.status) {
    if (deferred && cart) {
      deferred.resolve({
        cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'ecs', itemCount: qty },
      });
    }
    throw new Error(data.description || data.message || 'Could not add to bag');
  }
  if (deferred && cart) {
    deferred.resolve({
      cart: Events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'ecs', itemCount: qty, sections: data.sections, didError: false },
    });
  }
  await refreshCount();
  if (CFG.openCartOnAdd !== false) {
    setTimeout(() => {
      if (!openCartDrawer()) window.location.href = ROOT + 'cart';
    }, 60);
  }
  return data;
}

/* listen for cart changes made anywhere (Horizon drawer, quick add on search) */
eventsReady.then(() => {
  if (!Events || !Events.StandardEvents) return;
  document.addEventListener(Events.StandardEvents.cartLinesUpdate, (e) => {
    const p = e.promise && typeof e.promise.then === 'function' ? e.promise : Promise.resolve();
    p.then(() => refreshCount()).catch(() => refreshCount());
  });
});

/* quick add buttons on cards */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-sw-add]');
  if (!btn) return;
  e.preventDefault();
  if (btn.disabled) return;
  const id = btn.getAttribute('data-sw-add');
  if (!id) return;
  btn.classList.add('is-loading');
  try {
    await addToCart([{ id: Number(id), quantity: 1 }], btn);
  } catch (err) {
    const old = btn.textContent;
    btn.textContent = err.message;
    setTimeout(() => (btn.textContent = old), 2500);
  } finally {
    btn.classList.remove('is-loading');
  }
});

/* cart + search triggers in the custom header */
document.addEventListener('click', (e) => {
  const c = e.target.closest('[data-sw-open-cart]');
  if (c) {
    if (openCartDrawer()) e.preventDefault();
    return;
  }
  const s = e.target.closest('[data-sw-open-search]');
  if (s) {
    const modal = document.querySelector('#search-modal');
    if (modal && typeof modal.showDialog === 'function') {
      e.preventDefault();
      modal.showDialog();
    }
  }
});

/* ---------- reveal ---------- */
let io = null;
function initReveal(root) {
  const els = [...root.querySelectorAll('.sw-motion .sw-rv:not(.is-in)')];
  if (!els.length) return;
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }
  if (!io) {
    io = new IntersectionObserver(
      (entries) => entries.forEach((en) => {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      }),
      { threshold: 0.08, rootMargin: '0px 0px -4% 0px' }
    );
  }
  els.forEach((el) => {
    const sib = [...el.parentNode.children].filter((c) => c.classList.contains('sw-rv'));
    el.style.setProperty('--d', Math.min(sib.indexOf(el), 4) * 80 + 'ms');
    io.observe(el);
  });
}

/* ---------- announcement ---------- */
function initAnn(root) {
  root.querySelectorAll('[data-sw-ann]').forEach((bar) => {
    if (bar._swT) clearInterval(bar._swT);
    const msgs = [...bar.querySelectorAll('.sw-ann__msg')];
    if (msgs.length < 2) return;
    const delay = (Number(bar.dataset.swAnn) || 4.2) * 1000;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0;
    bar._swT = setInterval(() => {
      const cur = msgs[i];
      cur.classList.add('is-out');
      setTimeout(() => {
        cur.hidden = true;
        cur.classList.remove('is-out');
        i = (i + 1) % msgs.length;
        msgs[i].hidden = false;
      }, 300);
    }, delay);
  });
}

/* ---------- mobile menu drawer ---------- */
function initMenu(root) {
  root.querySelectorAll('[data-sw-menu]').forEach((dr) => {
    const id = dr.id;
    const openers = document.querySelectorAll(`[data-sw-menu-open="${id}"]`);
    const close = () => { dr.classList.remove('is-open'); dr.setAttribute('aria-hidden', 'true'); };
    const open = () => { dr.classList.add('is-open'); dr.setAttribute('aria-hidden', 'false'); const f = dr.querySelector('button, a'); f && f.focus(); };
    openers.forEach((o) => (o.onclick = open));
    dr.querySelectorAll('[data-sw-close]').forEach((c) => (c.onclick = close));
    dr.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  });
}

/* ---------- surf breaks tabs ---------- */
function initBreaks(root) {
  root.querySelectorAll('[data-sw-breaks]').forEach((wrap) => {
    const tabs = [...wrap.querySelectorAll('[role="tab"]')];
    const panels = [...wrap.querySelectorAll('[role="tabpanel"]')];
    const select = (t) => {
      tabs.forEach((x) => { const on = x === t; x.setAttribute('aria-selected', on); x.tabIndex = on ? 0 : -1; });
      panels.forEach((p) => (p.hidden = p.id !== t.getAttribute('aria-controls')));
    };
    tabs.forEach((t, i) => {
      t.onclick = () => select(t);
      t.onkeydown = (e) => {
        let n = null;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') n = tabs[(i + 1) % tabs.length];
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') n = tabs[(i - 1 + tabs.length) % tabs.length];
        if (n) { e.preventDefault(); n.focus(); select(n); }
      };
    });
  });
}

/* ---------- collection sort ---------- */
function initSort(root) {
  root.querySelectorAll('[data-sw-sort]').forEach((sel) => {
    sel.onchange = () => {
      const u = new URL(window.location.href);
      u.searchParams.set('sort_by', sel.value);
      u.searchParams.delete('page');
      window.location.href = u.toString();
    };
  });
}

/* ---------- modal (size guide) ---------- */
function initModals(root) {
  root.querySelectorAll('[data-sw-modal-open]').forEach((b) => {
    b.onclick = () => {
      const m = document.getElementById(b.getAttribute('data-sw-modal-open'));
      if (!m) return;
      m.classList.add('is-open');
      m._swReturn = b;
      const x = m.querySelector('.sw-modal__x');
      x && x.focus();
    };
  });
  root.querySelectorAll('.sw-modal').forEach((m) => {
    const close = () => { m.classList.remove('is-open'); m._swReturn && m._swReturn.focus(); };
    m.querySelectorAll('[data-sw-close]').forEach((c) => (c.onclick = close));
    m.onkeydown = (e) => { if (e.key === 'Escape') close(); };
  });
}

/* ---------- product page ---------- */
function initProduct(root) {
  root.querySelectorAll('[data-sw-product]').forEach((el) => {
    const dataEl = el.querySelector('script[data-sw-product-json]');
    if (!dataEl) return;
    const product = JSON.parse(dataEl.textContent);
    const variants = product.variants || [];
    const form = el.querySelector('[data-sw-form]');
    const idInput = el.querySelector('[data-sw-variant-id]');
    const atc = el.querySelector('[data-sw-atc]');
    const satcBtn = el.querySelector('[data-sw-satc-btn]');
    const priceEls = el.querySelectorAll('[data-sw-price]');
    const compareEls = el.querySelectorAll('[data-sw-compare]');
    const qtyInput = el.querySelector('[data-sw-qty]');
    const errEl = el.querySelector('[data-sw-err]');
    const gmain = el.querySelector('[data-sw-gmain]');
    const thumbs = [...el.querySelectorAll('[data-sw-thumb]')];
    const label = el.dataset.atcLabel || 'Add to bag — [price]';
    const soldLabel = el.dataset.soldLabel || 'Sold out';
    const unavailLabel = el.dataset.unavailLabel || 'Unavailable';
    const updateUrl = el.dataset.updateUrl !== 'false';
    let current = variants.find((v) => String(v.id) === String(idInput && idInput.value)) || variants[0];
    let selected = current ? current.options.slice() : [];

    const setMain = (src, srcset, alt) => {
      if (!gmain) return;
      let img = gmain.querySelector('img');
      if (!img) { img = document.createElement('img'); gmain.appendChild(img); }
      img.src = src; if (srcset) img.srcset = srcset; img.alt = alt || '';
    };
    thumbs.forEach((t) => {
      t.onclick = () => {
        thumbs.forEach((x) => x.setAttribute('aria-current', x === t));
        setMain(t.dataset.src, t.dataset.srcset, t.dataset.alt);
      };
    });

    const matches = (v, opts) => opts.every((o, i) => o == null || v.options[i] === o);

    const render = () => {
      // option buttons state
      el.querySelectorAll('[data-sw-opt]').forEach((grp) => {
        const idx = Number(grp.dataset.swOpt);
        grp.querySelectorAll('[data-sw-val]').forEach((b) => {
          const val = b.dataset.swVal;
          b.setAttribute('aria-pressed', selected[idx] === val);
          const test = selected.slice(); test[idx] = val;
          const avail = variants.some((v) => matches(v, test) && v.available);
          b.classList.toggle('is-oos', !avail);
        });
        const out = grp.querySelector('[data-sw-optval]');
        if (out) out.textContent = selected[idx] || '';
      });
      const v = current;
      if (idInput) idInput.value = v ? v.id : '';
      const price = v ? formatMoney(v.price) : '';
      priceEls.forEach((p) => (p.textContent = price));
      compareEls.forEach((c) => {
        if (v && v.compare_at_price && v.compare_at_price > v.price) { c.textContent = formatMoney(v.compare_at_price); c.hidden = false; }
        else c.hidden = true;
      });
      const txt = !v ? unavailLabel : v.available ? label.replace('[price]', price) : soldLabel;
      [atc, satcBtn].forEach((b) => {
        if (!b) return;
        b.disabled = !v || !v.available;
        const span = b.querySelector('[data-sw-atc-text]') || b;
        span.textContent = b === satcBtn ? (v && v.available ? (el.dataset.satcLabel || 'Add to bag') : soldLabel) : txt;
      });
      if (v && v.featured_media && v.featured_media.preview_image && el.dataset.variantImage !== 'false') {
        const t = thumbs.find((x) => x.dataset.mediaId === String(v.featured_media.id));
        if (t) t.click();
      }
    };

    el.querySelectorAll('[data-sw-opt]').forEach((grp) => {
      const idx = Number(grp.dataset.swOpt);
      grp.querySelectorAll('[data-sw-val]').forEach((b) => {
        b.onclick = () => {
          const val = b.dataset.swVal;
          const want = selected.slice(); want[idx] = val;
          let v = variants.find((x) => matches(x, want));
          if (!v) v = variants.find((x) => x.options[idx] === val && x.available) || variants.find((x) => x.options[idx] === val);
          current = v || null;
          selected = v ? v.options.slice() : want;
          render();
          if (v && updateUrl && !window.Shopify?.designMode) {
            const u = new URL(window.location.href);
            u.searchParams.set('variant', v.id);
            window.history.replaceState({}, '', u.toString());
          }
        };
      });
    });

    el.querySelectorAll('[data-sw-qty-step]').forEach((b) => {
      b.onclick = () => {
        if (!qtyInput) return;
        const n = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + Number(b.dataset.swQtyStep));
        qtyInput.value = n;
      };
    });

    const submit = async (btn) => {
      if (!current || !current.available) return;
      if (errEl) errEl.hidden = true;
      btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true');
      try {
        const qty = Math.max(1, parseInt(qtyInput ? qtyInput.value : 1, 10) || 1);
        await addToCart([{ id: current.id, quantity: qty }], btn);
      } catch (err) {
        if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
      } finally {
        btn.classList.remove('is-loading'); btn.removeAttribute('aria-busy');
      }
    };
    if (form) form.onsubmit = (e) => { e.preventDefault(); submit(atc); };
    if (satcBtn) satcBtn.onclick = (e) => { e.preventDefault(); submit(satcBtn); };

    // sticky add-to-bag bar on phones
    const bar = el.querySelector('[data-sw-satc]');
    const buy = el.querySelector('.sw-buy');
    if (bar && buy && 'IntersectionObserver' in window) {
      const ob = new IntersectionObserver(([en]) => {
        bar.classList.toggle('is-show', !en.isIntersecting && en.boundingClientRect.bottom < 0);
      });
      ob.observe(buy);
    }
    render();
  });
}

/* ---------- boot ---------- */
function init(root = document) {
  initReveal(root);
  initAnn(root);
  initMenu(root);
  initBreaks(root);
  initSort(root);
  initModals(root);
  initProduct(root);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();
document.addEventListener('shopify:section:load', (e) => init(e.target));
document.addEventListener('shopify:block:select', (e) => {
  const tab = e.target.closest && e.target.closest('[role="tabpanel"]');
  if (tab) {
    const t = document.querySelector(`[aria-controls="${tab.id}"]`);
    t && t.click();
  }
  const r = e.target.closest && e.target.closest('[data-sw-breaks] [role="tab"]');
  r && r.click();
});
